/**
 * Resources Hub — top-level category hub for /resources.
 * Cross-references all .md files under content/resources/
 *
 * Lists all 17 resources.
 */

export const resourcesHubMeta = {
  title: "Perception X2 Resources | Documentation, Guides, Whitepapers | CryptoMize",
  description:
    "Comprehensive resource library: documentation, implementation guides, whitepapers, case studies, webinars, technical papers, AI ethics, compliance, and migration playbooks for Perception X2.",
  canonical: "https://perception.ac/resources",
  slug: "resources",
};

export interface ResourceChild {
  code: string;
  title: string;
  description: string;
  href: string;
  icon: string;
  accent: "crimson" | "purple" | "cyan" | "emerald" | "amber" | "denim" | "violet" | "rose";
  category: "learning" | "reference" | "compliance" | "engagement";
  type: "guide" | "doc" | "paper" | "media" | "live";
}

export const resourcesList: ResourceChild[] = [
  {
    code: "R01",
    title: "Documentation",
    description: "Complete Perception X2 documentation: API reference, SDKs, architecture, deployment, integration patterns.",
    href: "/resources/documentation",
    icon: "BookOpen",
    accent: "crimson",
    category: "reference",
    type: "doc",
  },
  {
    code: "R02",
    title: "Implementation Guides",
    description: "Step-by-step guides for engineers, strategists, and operators — from initial integration to advanced compositions.",
    href: "/resources/guides",
    icon: "Compass",
    accent: "purple",
    category: "learning",
    type: "guide",
  },
  {
    code: "R03",
    title: "Whitepapers",
    description: "Long-form research and frameworks on perception engineering, narrative architecture, and amplification strategy.",
    href: "/resources/whitepapers",
    icon: "FileText",
    accent: "cyan",
    category: "reference",
    type: "paper",
  },
  {
    code: "R04",
    title: "Case Studies",
    description: "Real engagements, real outcomes — anonymized case studies across industries, use cases, and deployment scales.",
    href: "/resources/case-studies",
    icon: "Briefcase",
    accent: "emerald",
    category: "reference",
    type: "paper",
  },
  {
    code: "R05",
    title: "Technical Papers",
    description: "Deep technical papers on perception engineering algorithms, sentiment analysis, and resonance modeling.",
    href: "/resources/technical-papers",
    icon: "ScrollText",
    accent: "amber",
    category: "reference",
    type: "paper",
  },
  {
    code: "R06",
    title: "Blog",
    description: "Latest insights, platform updates, research findings, and field observations from the Perception X2 team.",
    href: "/resources/blog",
    icon: "Rss",
    accent: "denim",
    category: "engagement",
    type: "media",
  },
  {
    code: "R07",
    title: "Webinars",
    description: "Live and on-demand webinars — deep dives, product walkthroughs, customer conversations, and field briefings.",
    href: "/resources/webinars",
    icon: "Video",
    accent: "violet",
    category: "engagement",
    type: "live",
  },
  {
    code: "R08",
    title: "Videos",
    description: "Video library: product tours, capability walkthroughs, case study narratives, and architectural deep dives.",
    href: "/resources/videos",
    icon: "Film",
    accent: "rose",
    category: "engagement",
    type: "media",
  },
  {
    code: "R09",
    title: "Newsletter",
    description: "Monthly newsletter: platform updates, research highlights, field observations, and strategic briefings.",
    href: "/resources/newsletter",
    icon: "Mail",
    accent: "crimson",
    category: "engagement",
    type: "media",
  },
  {
    code: "R10",
    title: "AI Ethics",
    description: "Perception X2's AI ethics framework, principles, oversight, and the boundaries we maintain in every engagement.",
    href: "/resources/ai-ethics",
    icon: "Scale",
    accent: "emerald",
    category: "compliance",
    type: "paper",
  },
  {
    code: "R11",
    title: "Best Practices",
    description: "Operational best practices refined through 15+ years and 300+ elite engagements.",
    href: "/resources/best-practices",
    icon: "CheckCircle2",
    accent: "cyan",
    category: "learning",
    type: "guide",
  },
  {
    code: "R12",
    title: "Compliance Guide",
    description: "Regulatory and compliance guide — covering GDPR, CCPA, sector-specific obligations, and platform posture.",
    href: "/resources/compliance",
    icon: "FileCheck",
    accent: "amber",
    category: "compliance",
    type: "doc",
  },
  {
    code: "R13",
    title: "FAQ",
    description: "Comprehensive FAQ covering platform, services, capabilities, security, pricing, and operational questions.",
    href: "/resources/faq",
    icon: "HelpCircle",
    accent: "purple",
    category: "reference",
    type: "doc",
  },
  {
    code: "R14",
    title: "Glossary",
    description: "Domain glossary — terminology, jargon, and concepts spanning perception engineering, narrative strategy, and AI.",
    href: "/resources/glossary",
    icon: "Library",
    accent: "denim",
    category: "reference",
    type: "doc",
  },
  {
    code: "R15",
    title: "Industry Reports",
    description: "Industry research and analysis — perception trends, sector dynamics, and competitive landscape observations.",
    href: "/resources/industry-reports",
    icon: "TrendingUp",
    accent: "violet",
    category: "reference",
    type: "paper",
  },
  {
    code: "R16",
    title: "Migration Playbook",
    description: "Migration playbook — moving from legacy reputation, PR, or monitoring tools to Perception X2 without disruption.",
    href: "/resources/migration",
    icon: "ArrowRightLeft",
    accent: "rose",
    category: "learning",
    type: "guide",
  },
  {
    code: "R17",
    title: "Security Whitepaper",
    description: "Comprehensive security whitepaper — architecture, controls, certifications, incident history, and threat model.",
    href: "/resources/security-whitepaper",
    icon: "Lock",
    accent: "emerald",
    category: "compliance",
    type: "paper",
  },
];

export interface ResourceStat {
  value: string;
  label: string;
  to: number;
  suffix?: string;
  decimals?: number;
  icon: string;
  accent: "crimson" | "purple" | "cyan" | "emerald" | "amber" | "denim" | "violet";
}

export const resourcesStats: ResourceStat[] = [
  { value: "17", label: "Resource Verticals", to: 17, icon: "BookOpen", accent: "crimson" },
  { value: "4", label: "Content Categories", to: 4, icon: "Layers", accent: "purple" },
  { value: "300+", label: "Pages of Documentation", to: 300, suffix: "+", icon: "FileText", accent: "cyan" },
  { value: "100+", label: "Whitepapers & Papers", to: 100, suffix: "+", icon: "ScrollText", accent: "emerald" },
  { value: "50+", label: "Case Studies", to: 50, suffix: "+", icon: "Briefcase", accent: "amber" },
];

export interface ResourceFeature {
  title: string;
  description: string;
  icon: string;
  accent: "crimson" | "purple" | "cyan" | "emerald" | "amber" | "denim" | "violet" | "rose";
}

export const resourcesFeatures: ResourceFeature[] = [
  {
    title: "Comprehensive Coverage",
    description: "Every aspect of Perception X2 — platform, services, capabilities, security, ethics — documented in depth.",
    icon: "BookOpen",
    accent: "crimson",
  },
  {
    title: "Practitioner-Authored",
    description: "Resources are written by the engineers, strategists, and operators who build and run the platform.",
    icon: "UserCheck",
    accent: "purple",
  },
  {
    title: "Versioned & Maintained",
    description: "Documentation is versioned alongside the platform. Every release ships with corresponding doc updates.",
    icon: "GitBranch",
    accent: "cyan",
  },
  {
    title: "Multi-Format",
    description: "Long-form papers, quick-start guides, video walkthroughs, live webinars — format matched to learning need.",
    icon: "LayoutGrid",
    accent: "emerald",
  },
  {
    title: "Search & Discovery",
    description: "Every resource is searchable, taggable, and discoverable across the platform's knowledge graph.",
    icon: "Search",
    accent: "amber",
  },
  {
    title: "Open Access",
    description: "Public resources are open access. Sensitive content is gated to verified clients only.",
    icon: "Unlock",
    accent: "denim",
  },
  {
    title: "Continuous Updates",
    description: "Resources are updated continuously as the platform evolves. Editorial review on every change.",
    icon: "RefreshCcw",
    accent: "violet",
  },
  {
    title: "Translation Coverage",
    description: "Key resources are translated across major languages. Localized context preserved.",
    icon: "Languages",
    accent: "rose",
  },
];

export interface ResourceUseCase {
  title: string;
  body: string;
  icon: string;
  accent: "crimson" | "purple" | "cyan" | "emerald" | "amber" | "denim" | "violet" | "rose";
}

export const resourcesUseCases: ResourceUseCase[] = [
  {
    title: "Engineering Onboarding",
    body: "Documentation + implementation guides + API reference for engineers integrating Perception X2.",
    icon: "Code2",
    accent: "crimson",
  },
  {
    title: "Strategic Briefing",
    body: "Whitepapers + case studies + industry reports for executive decision-makers evaluating the platform.",
    icon: "Briefcase",
    accent: "purple",
  },
  {
    title: "Compliance & Security",
    body: "Compliance guide + security whitepaper + AI ethics for compliance, legal, and security teams.",
    icon: "ShieldCheck",
    accent: "emerald",
  },
  {
    title: "Continuous Learning",
    body: "Blog + webinars + videos + newsletter for ongoing education and platform updates.",
    icon: "GraduationCap",
    accent: "cyan",
  },
  {
    title: "Migration Planning",
    body: "Migration playbook + best practices + technical papers for teams transitioning from legacy tools.",
    icon: "ArrowRightLeft",
    accent: "amber",
  },
  {
    title: "Capability Discovery",
    body: "Capability-specific guides and case studies for organizations exploring specific Perception X2 capabilities.",
    icon: "Compass",
    accent: "violet",
  },
];

export interface ResourceWhy {
  title: string;
  body: string;
  metric?: string;
  accent: "crimson" | "purple" | "cyan" | "emerald" | "amber" | "denim" | "violet" | "rose";
}

export const resourcesWhy: ResourceWhy[] = [
  {
    title: "Practitioner-Authored",
    body: "Every resource is authored by the engineers, strategists, and operators who build and run the platform.",
    metric: "100%",
    accent: "crimson",
  },
  {
    title: "Version Synchronized",
    body: "Documentation is versioned alongside the platform. Every release ships with corresponding doc updates.",
    metric: "1:1",
    accent: "purple",
  },
  {
    title: "Continuous Updates",
    body: "Resources are updated continuously. Average editorial review turnaround: 48 hours.",
    metric: "48h",
    accent: "cyan",
  },
  {
    title: "Translation Coverage",
    body: "Key resources translated across 8 major languages. Localized context preserved.",
    metric: "8 langs",
    accent: "emerald",
  },
  {
    title: "Open Access",
    body: "Most resources are open access. Sensitive content is gated to verified clients only.",
    metric: "80%+",
    accent: "amber",
  },
  {
    title: "Multi-Format",
    body: "Long-form papers, quick-start guides, video walkthroughs, live webinars — format matched to learning need.",
    metric: "5 formats",
    accent: "denim",
  },
];

export interface ResourceFaq {
  q: string;
  a: string;
}

export const resourcesFaq: ResourceFaq[] = [
  {
    q: "What resources are available in the Perception X2 library?",
    a: "The Perception X2 resource library includes 17 verticals: documentation, implementation guides, whitepapers, case studies, technical papers, blog, webinars, videos, newsletter, AI ethics, best practices, compliance guide, FAQ, glossary, industry reports, migration playbook, and security whitepaper.",
  },
  {
    q: "Are the resources free to access?",
    a: "Most resources are open access. Sensitive content — including detailed security architecture and certain technical papers — is gated to verified clients. To request access, contact our team.",
  },
  {
    q: "How current is the documentation?",
    a: "Documentation is versioned alongside the platform. Every release ships with corresponding doc updates. Editorial review turnaround averages 48 hours.",
  },
  {
    q: "Can I request a specific resource?",
    a: "Yes. If you need a specific resource that is not currently in the library, our team can develop bespoke documentation for qualified engagements.",
  },
  {
    q: "Are resources available in multiple languages?",
    a: "Key resources are translated across 8 major languages. Localized context is preserved. Custom translations are available for enterprise engagements.",
  },
  {
    q: "How do I subscribe to the newsletter?",
    a: "Visit the Newsletter resource page and submit your email. Subscribers receive monthly platform updates, research highlights, and field observations.",
  },
  {
    q: "Can I use resources in my own materials?",
    a: "Resources are copyrighted. Limited quotation is permitted with attribution. Reproduction, redistribution, or commercial use requires written permission.",
  },
  {
    q: "How are case studies anonymized?",
    a: "Case studies are anonymized to protect client confidentiality. Specific outcomes, industries, and engagement patterns are preserved while identifying details are removed.",
  },
];

export const resourcesCta = {
  eyebrow: "Deepen Your Understanding",
  title: "17 resources. One knowledge base. The full Perception X2 library.",
  description:
    "Whether you are an engineer integrating the platform, a strategist briefing executives, or a compliance professional evaluating posture — the Perception X2 resource library has you covered.",
  primary: { label: "Browse Documentation", href: "/resources/documentation" },
  secondary: { label: "Subscribe to Newsletter", href: "/resources/newsletter" },
  trustItems: [
    "17 resource verticals",
    "4 content categories",
    "5 content formats",
    "Multi-language",
    "Practitioner-authored",
  ],
};
